import React from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../config/colors';
import { useAuth } from '../contexts/AuthContext';
import { obfuscateEmail } from '../utils/emailObfuscation';
import GoogleTranslate from './GoogleTranslate';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();

  const handleCVOLogin = () => {
    // Always navigate to CVO login page first
    // Login page will check if already authenticated
    navigate('/cvo-login');
  };

  const handleAuthorityLogin = () => {
    // Always navigate to Authority login page first
    // Login page will check if already authenticated
    navigate('/authority-login');
  };

  const handleCreateVigilance = () => {
    // Navigate directly to login page
    navigate('/vigilance-login');
  };

  const handleViewStatus = () => {
    navigate('/vigilance-status');
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top Header Bar - Light Blue Background - GIGW Compliant */}
      <div className="bg-primary-400 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-4">
            <a
              href="https://www.sjvn.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center hover:text-primary-600 duration-300"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span>Main Site</span>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <span>Digital India</span>
            <span>Great Place To Work</span>
            <span>PCMM/3</span>
          </div>
          <div className="flex items-center space-x-4">
            <GoogleTranslate />
          </div>
        </div>
      </div>

      {/* Main Header Section*/}
      <header
        className="relative shadow-sm overflow-hidden border-b border-primary-50 border-t border-primary-50"
        role="banner"
        style={{
          backgroundImage: 'url("/vigilance.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Stronger dark overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* SJVN Logo */}
              <div className="flex items-center py-2 bg-white bg-opacity-30 px-2">
                <img
                  src={`${process.env.PUBLIC_URL}/logo192.png`}
                  alt="SJVN Limited Logo"
                  className="w-18 h-24 mr-4"
                  role="img"
                  aria-label="SJVN Limited Logo"
                />
                <div>
                  <h1 className="text-2xl font-bold text-white notranslate">SJVN Limited</h1>
                  <p className="text-sm text-white notranslate" lang="hi">एसजेवीएन लिमिटेड</p>
                  <p className="text-xs text-white" >(A Joint Venture of Govt. of India & Govt. of Himachal Pradesh)</p>
                  <p className="text-xs text-white" >A Navratna PSU | ISO 9001:2015 Certified Company</p>
                  <p className="text-xs text-white">CIN: L40101HP1988G01008409</p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-primary-25">Vigilance Portal</h2>
            {isAuthenticated && user && (
              <div className="mt-2 bg-green-500 bg-opacity-90 px-3 py-1 rounded-lg">
                <p className="text-sm text-white">
                  ✓ Logged in as {user.name || obfuscateEmail(user.email)}
                </p>
              </div>
            )}
          </div>
        </div>

      </header >


      {/* Main Content Area - Clean Layout */}
      < main className="bg-gradient-to-br from-primary-50 to-white py-8 flex-1" id="main-content" role="main" >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">

          {/* Main Content - Login Options Grid */}
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary-400 mb-0">SJVN Vigilance Portal</h1>
              <p className="text-lg text-gray-600">Comprehensive platform for vigilance management and compliance monitoring</p>
              <div className="w-24 h-1 bg-primary-600 mx-auto mt-2"></div>
            </div>

            {/* Login Options - Single Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

              {/* Create Vigilance */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-primary-300 group">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-primary-400 to-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Create Vigilance Icon">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Lodge Complaint</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">Submit New Cases</p>
                  <button
                    onClick={handleCreateVigilance}
                    className="w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-500 hover:to-primary-600 transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md hover:shadow-lg"
                    aria-label="Create new vigilance case"
                  >
                    Lodge Complaint
                  </button>
                </div>
              </div>

              {/* View Status */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-primary-300 group">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-primary-400 to-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="View Status Icon">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Track Status</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">Track Cases</p>
                  <button
                    onClick={handleViewStatus}
                    className="w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-500 hover:to-primary-600 transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md hover:shadow-lg"
                    aria-label="View vigilance status"
                  >
                    Track Status
                  </button>
                </div>
              </div>

              {/* Authority Login */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-primary-300 group">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-primary-400 to-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="Authority Login Icon">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Authorized Users Login</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">Authorized Personnel Portal</p>
                  <button
                    onClick={handleAuthorityLogin}
                    className="w-full bg-gradient-to-r from-primary-400 to-primary-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-500 hover:to-primary-600 transition-all duration-300 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 shadow-md hover:shadow-lg"
                    aria-label="Login as Authority"
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'Authorized User Login'}
                  </button>
                </div>
              </div>

              {/* CVO Login */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 border border-gray-100 hover:border-primary-200 group">
                <div className="text-center">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" role="img" aria-label="CVO Login Icon">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">CVO Login</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">Chief Vigilance Officer Portal</p>
                  <button
                    onClick={handleCVOLogin}
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shadow-md hover:shadow-lg"
                    aria-label="Login as Chief Vigilance Officer"
                  >
                    {isAuthenticated ? 'Go to Dashboard' : 'CVO Login'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main >

      {/* Footer - GIGW Compliant */}
      < footer className="bg-primary-400 text-white py-4 border-t border-primary-50" role="contentinfo" >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Copyright and Additional Info */}
          <div>
            <div className="flex flex-col md:flex-row justify-between items-center text-sm">
              <div className="mb-2 md:mb-0">
                <p>Copyright © 2024 SJVN Ltd. | Website Last Updated: {new Date().toLocaleDateString('en-IN')}</p>
                {/* <p className="text-xs text-primary-200 mt-1">
                  Best viewed in Chrome, Firefox, Safari, Edge browsers | Screen Resolution: 1024x768 or higher
                </p> */}
              </div>
              {/* <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="bg-primary-700 px-3 py-2 rounded mb-2">
                    <span className="text-xs">Today's Stock</span>
                  </div>
                  <div className="text-xs">₹ 45.20</div>
                </div>
                <div className="text-center">
                  <div className="text-xs">Total Visitors:</div>
                  <div className="text-sm font-semibold">2,337,8286</div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </footer >
    </div >
  );
};

export default LandingPage;
